/**
 * timePeriodsCache - Lightweight time periods cache
 * 
 * CRITICAL: This is a PROXY to fwDataCache.loadTimePeriodsUnified to avoid duplication.
 * All actual caching happens in fwDataCache.ts for consistency.
 * 
 * Use this cache for:
 * - PlayerCoreStore initialization
 * - TimeProgressionService period lookups
 * - Any route that needs time periods but NOT full FreeWorld data
 * 
 * Features:
 * - 10 minute TTL
 * - In-flight promise deduplication
 * - No FreeWorld side effects
 */

import { loadTimePeriodsUnified, getCachedTimePeriodsUnified, invalidateTimePeriodsUnified } from '@/lib/fwDataCache';
import type { Tables } from '@/integrations/supabase/types';

type TimePeriod = Tables<'time_periods'>;

// ============= Diagnostic Logging =============
const DEBUG_TIME_CACHE = import.meta.env.DEV;

interface CacheStats {
  hits: number;
  misses: number;
  inFlightReuses: number;
}

const stats: CacheStats = {
  hits: 0,
  misses: 0,
  inFlightReuses: 0
};

/**
 * Load time periods with caching (proxy to fwDataCache)
 * Uses lightweight cache - does NOT trigger FreeWorld data loads
 */
export async function loadTimePeriodsLightweight(options?: {
  reason?: string;
  force?: boolean;
}): Promise<TimePeriod[]> {
  const { reason = 'unknown', force = false } = options || {};

  // Check if we already have cached data
  const cached = getCachedTimePeriodsUnified();
  if (!force && cached) {
    stats.hits++;
    if (DEBUG_TIME_CACHE) {
      console.debug(`[timePeriodsCache] HIT (proxy to fwDataCache) | reason=${reason}`);
    }
    return cached;
  }

  stats.misses++;
  if (DEBUG_TIME_CACHE) {
    console.debug(`[timePeriodsCache] FETCH (proxy to fwDataCache) | reason=${reason}`);
  }

  // Delegate to fwDataCache unified source
  const result = await loadTimePeriodsUnified({ reason, force });
  return result;
}

/**
 * Get cached time periods synchronously (no fetch)
 */
export function getCachedTimePeriodsLightweight(): TimePeriod[] | null {
  return getCachedTimePeriodsUnified();
}

/**
 * Invalidate the time periods cache
 */
export function invalidateTimePeriodsCache(): void {
  invalidateTimePeriodsUnified();
  if (DEBUG_TIME_CACHE) {
    console.debug('[timePeriodsCache] Cache invalidated (via fwDataCache)');
  }
}

/**
 * Get cache stats for debugging
 */
export function getTimePeriodsCacheStats(): CacheStats & { cacheAge: number | null } {
  // Can't get exact age since cache is in fwDataCache, but we track our own stats
  return {
    ...stats,
    cacheAge: null // Age tracking moved to fwDataCache
  };
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).__timePeriodsCache__ = {
    getStats: getTimePeriodsCacheStats,
    invalidate: invalidateTimePeriodsCache,
    getCached: getCachedTimePeriodsLightweight
  };
}
